import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";

/**
 * A browser's EventSource cannot attach an Authorization header, so the
 * org-scoped SSE streams (/sse/ai-credits, /sse/notifications, /sse/chatbot,
 * /sse/tasks, /sse/evaluation-reports) cannot authenticate the way every other
 * request does. They used to trust an `organizationId`/`userId` handed to them
 * in the query string, which meant anyone could name someone else's
 * organisation and receive its events.
 *
 * The backend now requires a short-lived signed token instead: this endpoint
 * mints one (after checking the caller really may view that organisation) and
 * the streams read organizationId/userId out of the token, never out of the
 * query string. This request is a normal authenticated call, so it carries the
 * Authorization header the stream itself cannot.
 */
export const sseApi = {
    /**
     * @returns the signed stream token. Valid for 60 seconds server side, so
     * mint one immediately before opening (or reopening) an EventSource rather
     * than holding on to it - see utils/sseStream.ts.
     * @throws the axios error, so callers can tell "no access" (403) from a
     * transient network failure worth retrying.
     */
    getStreamToken: async (
        organizationId: string,
        header: any
    ): Promise<string> => {
        const response = await AxiosRequest(
            "GET",
            `${configEnv.baseUrl}/sse/token?organizationId=${encodeURIComponent(
                organizationId
            )}`,
            null,
            header,
            // A background stream reconnecting must not raise a toast at the
            // user; the stream reports its own failures through onError.
            { silentError: true }
        );
        const token = response?.data?.data?.token;
        if (!token) {
            throw new Error("SSE token missing from response");
        }
        return token;
    },
};
